/* eslint-disable @typescript-eslint/naming-convention */
import React, {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    HTMLAttributes,
    memo,
    MouseEventHandler,
    ReactNode,
    useCallback,
    useMemo,
} from 'react';
import classNames from 'classnames';
import style from './style.module.scss';
import {TTypographyVariant, Typography} from 'components/Typography';
import {Loader, LOADER_COLOR, TLoaderColor} from 'components/Loader';
import {LinkProps} from 'next/link';
import {PREVENT_DEFAULT_HANDLER_MARKER, STOP_PROP_HANDLER_MARKER} from 'core/constants';
import {NEXT_PUBLIC_WINTER_DESIGN} from 'core/helpers/env';

export const BUTTON_SIZE = {
    large: 'large',
    medium: 'medium',
    small: 'small',
} as const;

export type TButtonSize = keyof typeof BUTTON_SIZE;

export const BUTTON_THEMES = {
    primary: 'primary',
    secondary: 'secondary',
    tertiary: 'tertiary',
    primary_inv: 'primary_inv',
    secondary_inv: 'secondary_inv',
    tertiary_inv: 'tertiary_inv',
    dark_transparent: 'dark_transparent',
} as const;

export type TButtonTheme = keyof typeof BUTTON_THEMES;

type TButtonSizeVariant = {
    DT: 'large' | 'medium' | 'small';
    TB: 'large' | 'medium' | 'small';
    MB: 'large' | 'medium' | 'small';
};

export interface IProps
    extends
        ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
        Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'rel'> {
    size?: TButtonSize;
    className?: string;
    theme?: TButtonTheme;
    isLoading?: boolean;
    disabled?: boolean;
    children?: ReactNode;

    // флаги состояния
    isStretched?: boolean;
    isWithoutPaddings?: boolean;
    isWithoutTextLoading?: boolean;
    /** Явно указывает, что кнопка содержит только SVG иконку */
    isIconButton?: boolean;

    // иконки
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;

    // стилизация
    textClassName?: string;

    // адаптивные размеры
    variants?: TButtonSizeVariant;

    // маркеры
    [PREVENT_DEFAULT_HANDLER_MARKER]?: boolean;
    [STOP_PROP_HANDLER_MARKER]?: boolean;

    // зимнее оформление NEW_YEAR_2026
    isWinterDesign?: boolean;
}

const mapSizeToTypographyVariant = (adaptiveSize: TButtonSize): TTypographyVariant => {
    const sizeMap: Record<TButtonSize, TTypographyVariant> = {
        small: 'label-14-r',
        medium: 'body-16-r',
        large: 'body-18-r',
    } as const;

    return sizeMap[adaptiveSize] || 'body-16-r';
};

const ButtonContent = memo<{
    isLoading: boolean;
    children: ReactNode;
    leftIcon: ReactNode;
    rightIcon: ReactNode;
    typographyVariant: TTypographyVariant;
    typographyVariants?: {
        DT: TTypographyVariant;
        TB: TTypographyVariant;
        MB: TTypographyVariant;
    };
    textClassName?: string;
    loaderColor?: TLoaderColor;
    isIconButton?: boolean;
}>(
    // eslint-disable-next-line complexity
    ({
        isLoading,
        children,
        leftIcon,
        rightIcon,
        typographyVariant,
        typographyVariants,
        textClassName,
        loaderColor,
        isIconButton = false,
    }) => {
        const loader = useMemo(
            () => (
                <div className={style.loader}>
                    <Loader color={loaderColor} />
                </div>
            ),
            [loaderColor],
        );

        return (
            <>
                {isLoading && leftIcon ? loader : leftIcon}

                {children && !isIconButton && (
                    <Typography
                        className={classNames(style.text, textClassName, {
                            [style.isTextHidden]: isLoading,
                        })}
                        variant={typographyVariant || typographyVariants?.DT}
                        variants={typographyVariants}
                    >
                        {children}
                    </Typography>
                )}

                {!isLoading && isIconButton && children}

                {isLoading && !leftIcon && !rightIcon && loader}

                {isLoading && rightIcon ? loader : rightIcon}
            </>
        );
    },
);

export const ButtonV3 = memo<IProps>(
    ({
        size = 'medium',
        theme = 'primary',
        isLoading = false,
        disabled = false,
        className,
        children,
        isStretched = false,
        isIconButton = false,
        leftIcon,
        rightIcon,
        type = 'button',
        onClick,
        href,
        target,
        rel,
        textClassName,
        variants,
        isWinterDesign = false,
        ...restButtonAttributes
    }) => {
        const handleClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = useCallback(
            (e) => {
                if (isLoading) {
                } else {
                    onClick?.(e);
                }
            },
            [isLoading, onClick],
        );

        const TargetElement = useMemo<'a' | 'button'>(() => {
            return href ? 'a' : 'button';
        }, [href]);

        const targetAttributes = useMemo<HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>>(() => {
            if ('a' === TargetElement) {
                return {
                    href,
                    rel,
                    target,
                } as HTMLAttributes<HTMLAnchorElement> & LinkProps;
            }

            return {
                type,
            } as HTMLAttributes<HTMLButtonElement>;
        }, [TargetElement, href, rel, target, type]);

        const responsiveSizeClasses = useMemo(() => {
            if (size && !variants) {
                return [style[size]];
            }
            if (variants) {
                return [style[`dt-${variants.DT}`], style[`tb-${variants.TB}`], style[`mb-${variants.MB}`]];
            }
            return [style.medium] as (string | undefined)[];
        }, [size, variants]);

        const loaderColor = useMemo(() => {
            const colors: Record<TButtonTheme, TLoaderColor> = {
                [BUTTON_THEMES.primary]: LOADER_COLOR.white,
                [BUTTON_THEMES.secondary]: LOADER_COLOR.black,
                [BUTTON_THEMES.tertiary]: LOADER_COLOR.black,
                [BUTTON_THEMES.primary_inv]: LOADER_COLOR.black,
                [BUTTON_THEMES.secondary_inv]: LOADER_COLOR.white,
                [BUTTON_THEMES.tertiary_inv]: LOADER_COLOR.white,
                [BUTTON_THEMES.dark_transparent]: LOADER_COLOR.white,
            };
            return colors[theme] || 'primary';
        }, [theme]);

        const typographyVariantProps = useMemo<
            | {variant: TTypographyVariant; variants?: undefined}
            | {variant?: undefined; variants: {DT: TTypographyVariant; TB: TTypographyVariant; MB: TTypographyVariant}}
        >(() => {
            if (size) {
                return {variant: mapSizeToTypographyVariant(size)};
            }

            if (variants) {
                return {
                    variants: {
                        DT: mapSizeToTypographyVariant(variants.DT),
                        TB: mapSizeToTypographyVariant(variants.TB),
                        MB: mapSizeToTypographyVariant(variants.MB),
                    },
                };
            }

            return {variant: mapSizeToTypographyVariant('medium')};
        }, [size, variants]);

        const buttonClasses = useMemo(
            () =>
                classNames('button-v3', 'simple-button', className, style.button, ...responsiveSizeClasses, {
                    [style.isLoading]: isLoading,
                    [style.isIconButton]: isIconButton,
                    [style.isStretched]: isStretched,
                    [style.isLeftIcon]: Boolean(leftIcon),
                    [style.isRightIcon]: Boolean(rightIcon),
                    [style[theme as string]]: Boolean(theme),
                    // NEW_YEAR_2026
                    'winter-design': isWinterDesign && NEXT_PUBLIC_WINTER_DESIGN,
                }),
            [
                className,
                responsiveSizeClasses,
                isLoading,
                isIconButton,
                isStretched,
                leftIcon,
                rightIcon,
                theme,
                isWinterDesign,
            ],
        );

        return (
            <TargetElement
                {...targetAttributes}
                {...restButtonAttributes}
                className={buttonClasses}
                disabled={disabled}
                onClick={handleClick}
            >
                <ButtonContent
                    isLoading={isLoading}
                    children={children}
                    leftIcon={leftIcon}
                    rightIcon={rightIcon}
                    typographyVariant={typographyVariantProps.variant || typographyVariantProps.variants?.DT}
                    typographyVariants={typographyVariantProps.variants}
                    textClassName={textClassName}
                    loaderColor={loaderColor}
                    isIconButton={isIconButton}
                />
            </TargetElement>
        );
    },
);
